import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import FaqFilter from './components/FaqFilter';
import FaqAccordion from './components/FaqAccordion';
import { qnaApi } from '../../services/qnaApi';
import { formatAiAnswer } from '../../utils/textFormat';
import { useDebounce } from '../../hooks/useDebounce';
import FaqListSkeleton from './components/FaqListSkeleton';

const FaqView = () => {
    const [inputSearch, setInputSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [openFaqId, setOpenFaqId] = useState(null);
    const [faqData, setFaqData] = useState([]);
    const size = 8;

    // useDebounce 훅 사용: inputSearch가 변해도 0.5초간 멈춰야 keyword가 업데이트됨
    const keyword = useDebounce(inputSearch, 500);

    // 검색어가 변경되면 페이지를 1로 리셋
    useEffect(() => {
        setPage(1);
    }, [keyword]);

    useEffect(() => {
        const fetchFaq = async () => {
            setIsLoading(true);
            try {
                const data = await qnaApi.getFaqList(page, size, '전체', keyword);

                if (data && data.items) {
                    const formattedData = data.items.map((item, index) => ({
                        id: item.id || `faq-${page}-${index}`,
                        title: item.title,
                        question: item.question,
                        answer: formatAiAnswer(item.answer)
                    }));

                    setFaqData(prev => page === 1 ? formattedData : [...prev, ...formattedData]);
                    setTotal(data.total || 0);
                }
            } catch (error) {
                console.error("FAQ 로드 실패:", error);
                if (page === 1) {
                    setFaqData([{
                        id: 'error-1',
                        title: 'API 로드 실패',
                        question: 'API 로드 실패 원인 파악',
                        answer: `에러 메시지: ${error.message} \nURL: /faq/list`
                    }]);
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchFaq();
    }, [page, keyword]);

    const toggleFaq = (id) => {
        setOpenFaqId(prevId => prevId === id ? null : id);
    };

    const loadMore = () => {
        setPage(prev => prev + 1);
    };

    return (
        <div className="space-y-6 pt-4 animate-fade-in pb-10">
            <FaqFilter
                searchTerm={inputSearch}
                setSearchTerm={setInputSearch}
            />
            {isLoading && page === 1 ? (
                <FaqListSkeleton count={size} />
            ) : (
                <FaqAccordion
                    filteredFaqs={faqData}
                    openFaqId={openFaqId}
                    toggleFaq={toggleFaq}
                    keyword={keyword}
                />
            )}
            {faqData.length < total && !isLoading && (
                <div className="flex justify-center mt-6">
                    <button
                        onClick={loadMore}
                        className="px-8 py-3 bg-white border border-gray-200 text-gray-600 rounded-full text-sm font-bold shadow-sm hover:bg-gray-50 focus:outline-none transition-colors"
                    >
                        더 많은 질문 보기 (+)
                    </button>
                </div>
            )}
            <div className="flex items-start gap-2 px-4 py-2 mt-4 text-gray-400">
                <Info size={16} className="flex-shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed">
                    본 FAQ는 <strong>중대재해처벌법 및 산업안전보건법에 의거하여 검토된 표준 답변</strong>입니다. 다만, 개별 현장의 특수한 상황에 따른 법적 판단이 필요할 경우 반드시 <strong>사내 안전 관리 부서 및 전문가와 상담</strong>하시기 바랍니다.
                </p>
            </div>
        </div>
    );
};

export default FaqView;